<script setup lang="ts">
import {Component} from "../types.ts";
import {onMounted, ref, watch} from "vue";
import useStore from "../store.ts";
import {renderInvalidationRegistry, RenderInvalidationReason} from "../utils/RenderInvalidation.ts";
import {enqueueRendererSrc} from "../worker/RenderWorkerManager.ts";
import {countdownDelta, updateCountdownHidden} from "../utils/ElementUtils.ts";
import {formatTime} from "../utils/FormatUtils.ts";

const props = defineProps<{
  item: Component
}>();

const store = useStore();


const deltaRef = ref(0);
const remainingSecondsRef = ref(0);

const updateDelta = (currentDate: number) => {
  const delta = countdownDelta(props.item, currentDate);
  remainingSecondsRef.value = delta == null ? 0 : Math.max(0, delta);
  deltaRef.value = delta == null ? 0 : Math.floor(delta / 60);
  // The tick is the only thing that can observe COUNTDOWN_REACHED crossing zero; the data-driven
  // half of the verdict is staged off-component by updateHidden (docs/EXTRACTED_COMMENTS.md#c759).
  updateCountdownHidden(props.item.props.id, currentDate);
};


// `@` is the remaining whole minutes (legacy). The remaining seconds are also read as an epoch
// timestamp and run through the DataObjectDigitalClock token set in UTC, so "HH:mm:ss" is the
// time left; clamped at 0 once reached.
const buildSrc = () => {
  const format = props.item.text_props?.format;
  if (format == null) return format;
  return formatTime(new Date(remainingSecondsRef.value * 1000), format, 2, "en", {timeZone: "UTC"})
      .replace("@", deltaRef.value.toString());
};

watch(() => Math.floor(store.currentDate / 1000), (newValue: number) => {
  updateDelta(newValue);
  enqueueRendererSrc(props.item, buildSrc());
});

watch(() => props.item.published.src, () => {
  updateDelta(Math.floor(store.currentDate / 1000));
  enqueueRendererSrc(props.item, buildSrc());
})

onMounted(() => {
  updateDelta(Math.floor(store.currentDate / 1000));
  enqueueRendererSrc(props.item, buildSrc());
  renderInvalidationRegistry.requestRender(
      RenderInvalidationReason.DataMutation,
      [props.item.props.id]
  );
});

</script>
<template>
  <div v-bind="item.props">
    <div :id="`countdown-${item.props.id}`" v-html="props.item.published.src" style="position: absolute">
    </div>
    <slot>
    </slot>

  </div>
</template>

<style scoped>

</style>